import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import { Sync } from "components/icons/Sync";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useDispatch } from "react-redux";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import QuestionIconTP from "static/images/QuestionIconTP.svg";
import {
  DefaultStreamChatGenerics,
  useChannelActionContext,
} from "stream-chat-react";
import { RequestedAnswersCardCustomData } from "types/Models/chatQnA";
import { pluralize } from "utils/pluralize";
import { AddRequestAnswers } from "./AddRequestAnswers";
import TicketResolve from "static/images/TicketResolve.svg";
import {
  ARCHIVED,
  REQUESTED,
  SUBMITTED,
  ACCEPTED,
  REVIEW_PENDING,
} from "constants/sensitiveData";
import { ReviewRequestAnswers } from "./ReviewRequestAnswers";

export const RequestAnswerChatCard = ({
  customData,
  groupId,
  size,
}: {
  customData: RequestedAnswersCardCustomData;
  groupId: string;
  size?: "regular" | "block";
}) => {
  const dispatch = useDispatch();
  const { isAdmin } = useRoleBasedView();
  const { jumpToMessage } = useChannelActionContext<DefaultStreamChatGenerics>(
    "RequestAnswerChatCard"
  );

  const isRequested = customData.card_status === REQUESTED;
  const isArchived = customData.card_status === ARCHIVED;
  const isReviewPending = customData.card_status === REVIEW_PENDING;
  const isClosed =
    customData.card_status === SUBMITTED || customData.card_status === ACCEPTED;
  const isFollowUp = Boolean(customData.metadata.is_follow_up);
  const totalQuestions = customData.metadata.requested_answers?.length;
  const viewAnswers = useModal();
  const reviewAnswers = useModal();

  const showReviewAction = isReviewPending && isAdmin;

  let headerText = isFollowUp ? "Follow-up requested" : "Information Requested";
  if (isClosed || isReviewPending) headerText = "Information Submitted";
  if (isClosed && isFollowUp) headerText = "Follow-up submitted";

  let bodyText = pluralize(
    totalQuestions,
    "question pending",
    "questions pending"
  );
  if (isClosed) bodyText = "Submission received";
  if (isClosed && isFollowUp) bodyText = "Follow-ups received";
  if (isReviewPending && isAdmin) {
    bodyText =
      pluralize(totalQuestions, "response", "responses") + " pending review";
  }
  if (isReviewPending && !isAdmin) {
    bodyText = "Submission received";
  }

  let actionText = isRequested ? "View & respond" : "View";
  if (showReviewAction) actionText = "Review Information";

  const handleActionClick = showReviewAction
    ? reviewAnswers.open
    : viewAnswers.open;

  const jumpToUpdatedRequest = () => {
    const latestMessageCardId = customData.metadata?.latest_message_card_id;
    if (!latestMessageCardId) return;
    dispatch(setScrollToMessageId(latestMessageCardId));
    jumpToMessage(latestMessageCardId);
  };

  return (
    <ChatCard.Root size={size}>
      <ChatCard.Header>
        <div className="t-gap-1.5 t-flex t-items-center">
          {isClosed || isReviewPending ? (
            <img src={TicketResolve} alt="TicketResolve" className="t-size-4" />
          ) : (
            <img src={QuestionIconTP} alt="QuestionIconTP" />
          )}
          <div>{headerText}</div>
        </div>
      </ChatCard.Header>
      <ChatCard.Body>
        <div className="t-ml-0.5 t-flex t-flex-col t-gap-4">
          <div className="t-text-subtext-sm t-text-text-60">{bodyText}</div>

          {isArchived ? (
            <div className="t-flex t-flex-col t-gap-2">
              <div className="t-flex t-items-center t-gap-1.5 t-text-blue-70 t-text-body-sm t-px-2 t-py-1 t-rounded t-bg-blue-10">
                <Sync />
                <div>The request has been updated.</div>
              </div>
              <Button block onClick={jumpToUpdatedRequest} size="small">
                Go to updated request
              </Button>
            </div>
          ) : (
            <Button
              customType={
                isRequested || showReviewAction ? "primary" : "secondary"
              }
              block
              onClick={handleActionClick}
              size="small"
            >
              {actionText}
            </Button>
          )}
        </div>
        {viewAnswers.isOpen && (
          <AddRequestAnswers
            {...viewAnswers}
            requestId={customData.metadata.answer_request_id}
            groupId={groupId}
          />
        )}
        {reviewAnswers.isOpen && (
          <ReviewRequestAnswers
            {...reviewAnswers}
            requestId={customData.metadata.answer_request_id}
            groupId={groupId}
          />
        )}
      </ChatCard.Body>
    </ChatCard.Root>
  );
};
